import {
  canHaveUnlimitedCredits,
  isSchoolTeacherRole,
  isStudentRole,
  isTeacherRole,
  isNonSchoolTeacherRole,
} from '../../utils/userAccessHelper';

/**
 * Bulk upload is a paid feature: Teacher Pro or a school plan, never the free
 * teacher tier. The signal is the server-computed credit meter rather than the
 * SKU string — a class teacher (school plan) and a JAI-TEA-PRO-* subscriber
 * both get unlimited credits, a JAI-TEA-FREE teacher and a community expert do
 * not — so this cannot disagree with the backend's own gate, which reads the
 * same field.
 */
export const hasBulkUploadPlan = (profile) =>
  isTeacherRole(profile) && canHaveUnlimitedCredits(profile);

export const canAccessSchoolTeacherBulkUpload = (profile) =>
  Boolean(isSchoolTeacherRole(profile));

export const canAccessIndependentTeacherBulkUpload = (profile) =>
  Boolean(isNonSchoolTeacherRole(profile)) && hasBulkUploadPlan(profile);

/** School-teacher-only access (legacy name used by existing callers). */
export const canAccessBulkUpload = canAccessSchoolTeacherBulkUpload;

/** Teachers that can open bulk upload from My Drafts. */
export const canAccessAnyBulkUpload = (profile) =>
  canAccessSchoolTeacherBulkUpload(profile) ||
  canAccessIndependentTeacherBulkUpload(profile);

/**
 * A teacher who can see bulk upload but whose plan does not include it. The
 * entry point stays visible for them — selecting it opens the upgrade prompt
 * rather than the wizard — so the feature is discoverable, not hidden.
 */
export const isBulkUploadPlanLocked = (profile) =>
  isTeacherRole(profile) && !canAccessAnyBulkUpload(profile);

/** Every teacher sees the entry; the plan decides what selecting it does. */
export const canSeeBulkUploadEntry = (profile) =>
  canAccessAnyBulkUpload(profile) || isBulkUploadPlanLocked(profile);

export const isTaskClosed = (task) =>
  String(task?.status || '').toUpperCase() === 'CLOSED';

/**
 * A closed task takes no more work, so every entry point that would start an
 * upload run for it is withdrawn. Absent task means "not an assignment at all"
 * (a My Drafts document), which is never closed and keeps bulk upload.
 */
export const canBulkUploadIntoTask = (task) => !isTaskClosed(task);

/** Used for My Drafts chrome (not bulk upload access). */
export const isStudentMyDraftsUser = (profile) => isStudentRole(profile);

export const getBulkUploadVariant = (profile) => {
  if (canAccessSchoolTeacherBulkUpload(profile)) return 'schoolTeacher';
  if (canAccessIndependentTeacherBulkUpload(profile)) return 'independentTeacher';
  return null;
};
